'use client';
/**
 * Journal RH — les actes du module, en remontant le temps.
 *
 * ── Pourquoi UN écran, et non un fragment dans chacun des quatre ────────────
 *
 * Le journal traverse Présences, Absences, Paie et Habilitations. Il a
 * longtemps semblé qu'il fallait donc le poser dans les quatre. C'est
 * l'inverse : un journal se consulte en remontant le temps — « qu'est-ce qui
 * s'est passé sur cette personne depuis lundi » —, pas en fouillant quatre
 * écrans dont aucun ne montre les trois autres. Et un fragment dupliqué quatre
 * fois finit par dire quatre choses différentes.
 *
 * Ce qui rend l'écran unique utilisable, ce sont les filtres : ils répondent
 * aux questions que les quatre écrans posaient — par personne, par nature
 * d'acte, par période.
 *
 * ── Ce qu'il lit, et par quelle porte ───────────────────────────────────────
 *
 * `GET ged/rh/journal`, la route GARDÉE (`exigerRh` côté contrôleur, rubrique
 * « Journal RH » côté section). ⚠️ Ne JAMAIS le réécrire sur `ged-events` par
 * le CRUD générique : celui-ci n'a aucune des deux gardes, et cet écran
 * rassemble en clair les traces que les rubriques Paie et Absences protègent
 * une à une.
 *
 * Aucune écriture : il n'existe volontairement aucun geste ici. Un journal qui
 * s'édite ne prouve rien.
 */
import React from 'react';
import { useGed } from '@/lib/store';
import type { GedEvent } from '@/lib/types';
import { fmtDateHeure, fmtDate, jourIso, isoJour } from '@/lib/meta';
import { I, TitrePage, card, inputStyle, FilterBar, PAR_PAGE } from '@/components/ui';
import { Tableau } from '@/shared/Tableau';
import { AccesRefuseRh, lbl } from '@/components/screens/RhAbsences';

import { Bouton } from '@/shared/Bouton';
/**
 * Natures d'acte — les familles, pas les 31 types bruts.
 *
 * Un filtre à 31 entrées ne se lit pas, et les types techniques
 * (`LigneGrilleEvaluee`, `StatutPersonnelModifie`) ne sont pas le vocabulaire
 * de la gestionnaire. On regroupe donc par question posée.
 *
 * ⚠️ Le rattachement se fait par PRÉFIXE de type, jamais par liste exhaustive :
 * un type ajouté demain par un lot RH tomberait sinon dans « aucune famille »
 * et disparaîtrait du filtre en silence — la seule faute qu'un journal ne peut
 * pas se permettre. Ce qui ne correspond à rien reste visible sous « Autres ».
 */
const FAMILLES: { code: string; label: string; prefixes: string[] }[] = [
  { code: 'personnel', label: 'Personnel & contrats', prefixes: ['Personnel', 'Contrat', 'Statut', 'Compte'] },
  { code: 'absences', label: 'Absences & congés', prefixes: ['Absence', 'Conge'] },
  { code: 'presences', label: 'Présences & pointage', prefixes: ['Presence', 'Pointage', 'Journee'] },
  // `Tache` (25/08/2026) : sans ce préfixe, « TacheNouvelleAMaitriser »
  // tomberait sous « Autres » — un acte tracé mais rangé nulle part.
  { code: 'habilitations', label: 'Habilitations & grilles', prefixes: ['Habilitation', 'Grille', 'LigneGrille', 'Avis', 'Dossier', 'Decision', 'Tache'] },
  /* `Planning` et `Derogation` (24/08/2026) : la réunification des clés
     `RH-PLANNING`/`RH-HABILITATIONS` fait arriver `PlanningGenere` et
     `PlanningDuplique` ici — sans préfixe ils tombaient sous « Autres ». */
  { code: 'postes', label: 'Postes & plannings', prefixes: ['Poste', 'Affectation', 'Ouverture', 'Periode', 'Planning', 'Derogation'] },
  { code: 'integration', label: 'Intégration & accueil', prefixes: ['Integration', 'Pochette', 'PreEmbauche'] },
  { code: 'paie', label: 'Paie', prefixes: ['Paie', 'Variable'] },
  { code: 'parametrage', label: 'Paramétrage & reprises', prefixes: ['Parametrage', 'Reprise'] },
];

function familleDe(evType: string): string {
  const f = FAMILLES.find((x) => x.prefixes.some((p) => evType.startsWith(p)));
  return f ? f.code : 'autres';
}

/** Libellé lisible d'un type technique : « AbsenceJustificatifConsulte » →
 *  « Absence justificatif consulté ». Le type brut reste affiché à côté, en
 *  petit : c'est lui qu'on cite dans une requête en base. */
function libelleType(evType: string): string {
  const mots = evType.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
  return mots.charAt(0).toUpperCase() + mots.slice(1).toLowerCase();
}

const jourDe = (ts: string) => jourIso(ts || '');

export function RhJournal() {
  const rhAcces = useGed((s) => s.rhAcces);
  const rhJournal = useGed((s) => s.rhJournal);
  const loadJournalRh = useGed((s) => s.loadJournalRh);
  const rhChargement = useGed((s) => s.rhChargement);

  const [q, setQ] = React.useState('');
  const [famille, setFamille] = React.useState('toutes');
  /* ⚠️ LA FENÊTRE PAR DÉFAUT EST DE 30 JOURS (relevé du 27/08/2026, P2).
     Le journal montait ses 692 entrées d'un bloc — 46 154 px, quarante-sept
     écrans — et le filtre de dates, seul moyen de réduire, arrivait vide :
     deux champs `jj/mm/aaaa` qu'il fallait remplir pour que la page devienne
     lisible. Personne ne remplit un filtre pour voir ce qu'il vient d'ouvrir.
     Les 30 derniers jours sont ce qu'on relit en routine ; le champ reste
     modifiable, et le vider redonne tout l'historique. */
  const [du, setDu] = React.useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return isoJour(d);
  });
  const [au, setAu] = React.useState('');

  // Chargé au montage, et rechargeable à la demande. Le tableau de dépendances
  // ne porte que l'action du magasin (stable) : y mettre `rhJournal`
  // relancerait la lecture à chaque réponse.
  React.useEffect(() => { void loadJournalRh(); }, [loadJournalRh]);

  const evenements: GedEvent[] = React.useMemo(() => rhJournal ?? [], [rhJournal]);

  const parFamille = React.useMemo(() => {
    const n: Record<string, number> = {};
    for (const e of evenements) {
      const f = familleDe(e.evType);
      n[f] = (n[f] || 0) + 1;
    }
    return n;
  }, [evenements]);

  const liste = React.useMemo(() => {
    const qq = q.trim().toLowerCase();
    return evenements.filter((e) => {
      if (famille !== 'toutes' && familleDe(e.evType) !== famille) return false;
      const j = jourDe(e.ts);
      if (du && j < du) return false;
      if (au && j > au) return false;
      if (!qq) return true;
      // La personne concernée vit dans le LIBELLÉ (les événements ne portent pas
      // de champ « personne » : le journal est chaîné, pas relationnel). La
      // recherche couvre donc le libellé, l'acteur et le type — et c'est dit à
      // l'écran plutôt que laissé deviner.
      return `${e.payload} ${e.actorName} ${e.actor} ${e.evType}`.toLowerCase().includes(qq);
    });
  }, [evenements, q, famille, du, au]);

  const session = useGed((st) => st.session);
  if (!rhAcces?.acces) return <AccesRefuseRh />;

  const periodeActive = !!(du || au);
  const chargeUneFois = rhJournal !== null;

  return (
    <section>
      <TitrePage eyebrow={<>Personnel &amp; plannings</>} titre="Journal RH"
        sousTitre={<>
          Tous les actes du module RH — embauche, congé décidé, présence corrigée, habilitation
          accordée, justificatif consulté — dans l’ordre où ils se sont produits. La trace est
          signée et chaînée&nbsp;: elle se lit, elle ne se modifie pas.
        </>} />

      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap', margin: '16px 0 12px' }}>
        <div>
          <label style={lbl} htmlFor="journal-du">Du</label>
          <input id="journal-du" type="date" value={du} onChange={(e) => setDu(e.target.value)} style={{ ...inputStyle, width: 160 }} />
        </div>
        <div>
          <label style={lbl} htmlFor="journal-au">Au</label>
          <input id="journal-au" type="date" value={au} onChange={(e) => setAu(e.target.value)} style={{ ...inputStyle, width: 160 }} />
        </div>
        {periodeActive && (
          <Bouton taille="sm" onClick={() => { setDu(''); setAu(''); }}>
            Toute la période
          </Bouton>
        )}
        <Bouton taille="sm" onClick={() => void loadJournalRh()} title="Relire le journal">
          <I n="refresh" /> Actualiser
        </Bouton>
      </div>

      <FilterBar
        q={q} onQ={setQ}
        placeholder="Rechercher une personne, un acteur, un libellé…"
        chips={[
          { k: 'toutes', label: 'Toutes', n: evenements.length },
          ...FAMILLES.filter((f) => parFamille[f.code]).map((f) => ({ k: f.code, label: f.label, n: parFamille[f.code] })),
          ...(parFamille.autres ? [{ k: 'autres', label: 'Autres', n: parFamille.autres }] : []),
        ]}
        actif={famille} onChip={setFamille}
        total={evenements.length} affiches={liste.length}
        onReset={() => { setQ(''); setFamille('toutes'); setDu(''); setAu(''); }} />

      {(
        /* ⚠️ `Tableau` et non une grille de `<div>` (Inès, 28/08/2026).
            Ce journal était une grille : quatre colonnes dessinées à la main,
            une fausse ligne d'en-tête, et donc RIEN de ce qu'un tableau donne —
            ni tri, ni en-tête collant, ni déplacement des colonnes. Sur 692
            entrées, c'est l'écran du module où ça manquait le plus : on prépare
            un audit en cherchant « qui a fait quoi », et l'ordre chronologique
            est le seul qu'on avait.
            Le tri porte sur la donnée BRUTE (`valeur`) et non sur le rendu :
           trier « Quand » sur le texte affiché classerait le 10 avant le 9. */
        <div style={{ ...card, padding: 0, overflow: 'hidden', marginTop: 12 }}>
          <Tableau
            id="rh-journal"
            ajusterLargeur
            /* L5 (10/10/2026) : niveau AVANCÉ (18 757 actes) — « Quand » épinglé,
               filtres par colonne (date du/au, nature et auteur en liste), export
               de référence, états intégrés (squelette, échec de lecture +
               Réessayer, vide). L'heure passe par `fmtDateHeure` : jour ET heure
               lus dans le même fuseau (reliquat L-1 sur `fmtDateTime`). */
            niveau="avance"
            chargement={!chargeUneFois && rhChargement > 0}
            erreur={!chargeUneFois && rhChargement === 0 ? 'Le journal n’a pas pu être lu. La lecture est réservée au gestionnaire RH ; si vous portez ce rôle, le moteur n’a peut-être pas répondu.' : null}
            onReessayer={() => void loadJournalRh()}
            vide={{
              titre: evenements.length === 0 ? 'Aucun acte enregistré.' : 'Aucun acte ne correspond à ces critères.',
              indice: evenements.length === 0
                ? 'Le journal se remplit tout seul, au fil des décisions prises dans les écrans du personnel.'
                : 'Élargissez la période, ou revenez à « Toutes ».',
              action: evenements.length > 0
                ? <Bouton taille="sm" onClick={() => { setQ(''); setFamille('toutes'); setDu(''); setAu(''); }}>Réinitialiser les filtres</Bouton>
                : undefined,
            }}
            export={{
              registre: 'journal-rh', libelleRegistre: 'Journal RH', auteur: session.name || session.key,
              filtres: [q.trim() ? `Recherche « ${q.trim()} »` : '', famille !== 'toutes' ? `Famille : ${famille}` : '', du ? `Du ${fmtDate(du)}` : '', au ? `Au ${fmtDate(au)}` : ''].filter(Boolean),
            }}
            lignes={liste}
            cleLigne={(e) => e.documentId}
            parPage={PAR_PAGE}
            quoi="actes"
            colonnes={[
              {
                cle: 'quand', libelle: 'Quand', type: 'date', epinglee: true, obligatoire: true, export: true,
                valeur: (e) => e.ts || '',
                exporter: (e) => fmtDateHeure(e.ts),
                style: { fontFamily: 'var(--font-mono)', fontSize: 12.5, whiteSpace: 'nowrap', width: 150 },
                rendu: (e) => fmtDateHeure(e.ts),
              },
              {
                cle: 'nature', libelle: 'Nature de l’acte', type: 'liste', export: true,
                valeur: (e) => libelleType(e.evType),
                style: { width: 210 },
                rendu: (e) => (
                  <>
                    <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: 'var(--text-strong)' }}>{libelleType(e.evType)}</span>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--text-faint)' }}>{e.evType}</span>
                  </>
                ),
              },
              {
                cle: 'quoi', libelle: 'Ce qui a été fait', export: true,
                valeur: (e) => e.payload || '',
                style: { whiteSpace: 'normal', wordBreak: 'break-word', minWidth: 260 },
                rendu: (e) => e.payload || '—',
              },
              {
                cle: 'qui', libelle: 'Par qui', type: 'liste', export: true,
                valeur: (e) => e.actorName || e.actor || '',
                style: { width: 160 },
                rendu: (e) => (
                  <>
                    {e.actorName || e.actor || '—'}
                    {/* Un maillon non signé n'est pas un maillon rompu — mais il
                        ne vaut pas preuve, et l'auditeur doit le voir sans
                        requête. */}
                    {!e.signature && (
                      <span title="Acte enregistré sans signature" style={{ marginLeft: 6, color: 'var(--text-faint)' }}><I n="alert" /></span>
                    )}
                  </>
                ),
              },
            ]}
          />
        </div>
      )}
    </section>
  );
}
